import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { getElectricalPanelById } from '../api/electricalPanels'
import { getTerminalBlocks, createTerminalBlock, updateTerminalBlock, deleteTerminalBlock, type TerminalBlock } from '../api/electricalPanels'
import { useAuth } from '../AuthContext'
import { PermissionRow } from '../api/admin'
import { apiClient } from '../api/client'

interface TerminalBlockModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { name: string; description: string }) => void
  terminalBlock?: TerminalBlock
  isLoading: boolean
}

function TerminalBlockModal({ isOpen, onClose, onSubmit, terminalBlock, isLoading }: TerminalBlockModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  })

  // Update form data when terminalBlock changes
  useEffect(() => {
    if (terminalBlock) {
      setFormData({
        name: terminalBlock.name || '',
        description: terminalBlock.description || ''
      })
    } else {
      setFormData({
        name: '',
        description: ''
      })
    }
  }, [terminalBlock])

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
      return () => document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">
          {terminalBlock ? 'Редактировать блок клеммников' : 'Создать блок клеммников'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название блока
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Блок клеммников XT1"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Описание назначения блока"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Сохранение...' : (terminalBlock ? 'Обновить' : 'Создать')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function TerminalBlocksPage() {
  const { projectId, panelId } = useParams<{ projectId: string; panelId: string }>()
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBlock, setEditingBlock] = useState<TerminalBlock | undefined>()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: panel, isLoading: panelLoading } = useQuery({
    queryKey: ['electrical-panel', panelId],
    queryFn: () => getElectricalPanelById(Number(panelId)),
    enabled: !!panelId,
  })

  // Проверка прав пользователя на редактирование проекта
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  // Загрузка всех permissions для текущего пользователя и проекта
  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Helper функция для получения эффективного права на ресурс (с учётом наследования)
  const getEffectivePermission = (
    resourceType: 'electrical_panel' | 'schema',
    resourceId: number | null,
  ): string | null => {
    if (currentUser?.role === 'system_admin') {
      return 'manage'
    }

    if (!projectId || !currentUser?.id) return null

    // Проверяем явное permission
    const explicit = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        p.resource_type === resourceType &&
        p.resource_id === (resourceId ? String(resourceId) : null) &&
        p.permission !== 'deny',
    )
    if (explicit) {
      return explicit.permission
    }

    // Проверяем наследование от роли проекта
    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      const roleMap: Record<string, string> = {
        viewer: 'view',
        editor: 'edit',
        project_admin: 'manage',
      }
      return roleMap[access.role_in_project] || null
    }

    return null
  }

  // Helper функция для проверки, может ли пользователь редактировать панель
  const canEditPanel = (): boolean => {
    if (currentUser?.role === 'system_admin') return true
    if (!panelId) return false
    const perm = getEffectivePermission('electrical_panel', Number(panelId))
    return perm === 'edit' || perm === 'manage'
  }

  const canEdit = canEditPanel()

  const { data: terminalBlocks, isLoading: blocksLoading } = useQuery({
    queryKey: ['terminal-blocks', panelId],
    queryFn: () => getTerminalBlocks(Number(panelId)),
    enabled: !!panelId,
  })

  const createBlockMutation = useMutation({
    mutationFn: createTerminalBlock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-blocks', panelId] })
      handleCloseModal()
    },
  })

  const updateBlockMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TerminalBlock> }) => 
      updateTerminalBlock(id, {
        name: data.name || '',
        description: data.description,
        position_in_panel: data.position_in_panel
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-blocks', panelId] })
      handleCloseModal()
    },
  })

  const deleteBlockMutation = useMutation({
    mutationFn: deleteTerminalBlock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terminal-blocks', panelId] })
    },
  })

  const handleCreateBlock = async (blockData: { name: string; description: string }) => {
    if (!panelId) return
    
    try {
      if (editingBlock) {
        await updateBlockMutation.mutateAsync({
          id: editingBlock.id,
          data: blockData
        })
      } else {
        await createBlockMutation.mutateAsync({
          electrical_panel_id: Number(panelId),
          ...blockData
        })
      }
    } catch (error) {
      console.error('Error saving terminal block:', error)
    } finally {
      handleCloseModal()
    }
  }

  // Function to handle clicks
  const handleEditBlock = (block: TerminalBlock) => {
    setEditingBlock(block)
    setIsModalOpen(true)
  }

  const handleDeleteBlock = (blockId: number) => {
    if (confirm('Вы уверены, что хотите удалить этот блок клеммных терминалов?')) {
      deleteBlockMutation.mutate(blockId)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingBlock(undefined)
  }

  /** Схема по ID (`/schemas/69`) — полный редактор с перемычками; `terminal?blockId=` — legacy-режим. */
  const getTerminalBlockSchemaPath = (block: TerminalBlock): string => {
    if (block.schema_id != null && block.schema_id > 0) {
      return `/projects/${projectId}/schemas/${block.schema_id}`
    }
    return `/projects/${projectId}/schemas/terminal?blockId=${block.id}`
  }

  const navigateToTerminalBlockSchema = (block: TerminalBlock) => {
    navigate(getTerminalBlockSchemaPath(block))
  }

  if (projectLoading || panelLoading) {
    return <div className="text-center py-8">Загрузка...</div>
  }

  if (!project || !panel) {
    return <div className="text-center py-8 text-red-600">Проект или щит не найден</div>
  }

  const blocksList = terminalBlocks || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к {project.name}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            🔗 Блоки клеммных терминалов
            <span className="text-lg font-normal text-gray-600 ml-2">
              → {panel.proj_id} ({panel.name})
            </span>
          </h1>
          <p className="text-gray-600 mt-1">
            Управление блоками клеммных терминалов щита {panel.proj_id}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditingBlock(undefined)
              setIsModalOpen(true)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Добавить блок
          </button>
        )}
      </div>

      {/* Terminal Blocks Table */}
      {blocksLoading ? (
        <div className="text-center py-8">Загрузка блоков клеммников...</div>
      ) : blocksList.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Тип
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Кол-во
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Описание
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {blocksList.map((block) => (
                  <tr key={block.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={getTerminalBlockSchemaPath(block)}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                        title="Открыть схему"
                      >
                        {block.name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                        Стандартный
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded">
                        {block.instances_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {block.description || 'Нет описания'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => navigateToTerminalBlockSchema(block)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Открыть схему"
                      >
                        📊
                      </button>
                      {canEdit && (
                        <>
                          <button
                            onClick={() => navigate(`/projects/${projectId}/panels/${panelId}/terminal-blocks/${block.id}/instances`)}
                            className="text-green-600 hover:text-green-900"
                            title="Управление экземплярами"
                          >
                            🔌
                          </button>
                          <button
                            onClick={() => handleEditBlock(block)}
                            className="text-yellow-600 hover:text-yellow-800 px-2 py-1"
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteBlock(block.id)}
                            className="text-red-600 hover:text-red-800 px-2 py-1"
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4 p-4">
            {blocksList.map((block) => (
              <div key={block.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium">
                      <Link
                        to={getTerminalBlockSchemaPath(block)}
                        className="text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                        title="Открыть схему"
                      >
                        {block.name}
                      </Link>
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                        Стандартный
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        {block.instances_count || 0} экз.
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => navigateToTerminalBlockSchema(block)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Схема"
                    >
                      📊
                    </button>
                    {canEdit && (
                      <>
                        <button
                          onClick={() => navigate(`/projects/${projectId}/panels/${panelId}/terminal-blocks/${block.id}/instances`)}
                          className="text-green-600 hover:text-green-900"
                          title="Управление экземплярами"
                        >
                          🔌
                        </button>
                        <button
                          onClick={() => handleEditBlock(block)}
                          className="text-yellow-600 hover:text-yellow-800 px-2 py-1"
                          title="Редактировать"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteBlock(block.id)}
                          className="text-red-600 hover:text-red-800 px-2 py-1"
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {block.description && (
                  <p className="text-sm text-gray-600">{block.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow-md">
          <p className="mb-4">Нет блоков клеммных терминалов для этого щита</p>
          {canEdit && (
            <button
              onClick={() => {
                setEditingBlock(undefined)
                setIsModalOpen(true)
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Создать первый блок
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      <TerminalBlockModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreateBlock}
        terminalBlock={editingBlock}
        isLoading={createBlockMutation.isPending || updateBlockMutation.isPending}
      />
    </div>
  )
} 